import Store from '@ember-data/store';
import { action, set, setProperties } from '@ember/object';
import { service } from '@ember/service';
import { isEmpty, isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { type SelectOption } from 'donor-elf-web/lib/form-component-utils';
import JournalContactInfo from 'donor-elf-web/lib/models/journal-contact-info';
import JournalDecisionInfo from 'donor-elf-web/lib/models/journal-decision-info';
import {
  calculateEndDateForOneTimePledge,
  pledgeFrequencyLabels,
  pledgeMonthlyFrequency,
  pledgeOneTimeFrequency,
} from 'donor-elf-web/lib/pledge-utils';
import JournalGoalModel from 'donor-elf-web/models/journal-goal';
import PledgeModel from 'donor-elf-web/models/pledge';
import AbilitiesService from 'donor-elf-web/services/abilities';
import journalDecisionChangeValidation from 'donor-elf-web/validations/journal-decision-change';
import Changeset from 'ember-changeset';
import lookupValidator from 'ember-changeset-validations';

interface JournalCellDecisionModalArgs {
  journalContactInfo: JournalContactInfo;
  journalDecisionInfo: JournalDecisionInfo;
  onElementHeightChanged: () => void;
  onSaved: () => void;
  onCancel: () => void;
}

export default class JournalCellDecisionModal extends Component<JournalCellDecisionModalArgs> {
  @service declare abilities: AbilitiesService;
  @service declare fundGoalService: any;
  @service declare store: Store;

  @tracked askAboutExistingPledge = false;
  @tracked existingPledgeDecision?: 'existing' | 'increase' | 'new';
  @tracked askWhichPledgeToIncreaseFrom = false;
  @tracked askWhichPledgeToIncreaseTo = false;
  @tracked increaseFromPledge?: PledgeModel;
  @tracked increaseToPledge?: PledgeModel;
  @tracked saving = false;
  @tracked showValidationErrors = false;
  @tracked saveError = null;
  @tracked pledgeIsNotPartOfAnotherDecision?: boolean;
  @tracked disableSave = false;

  _changeset?: any;

  get journalContact() {
    return this.args.journalContactInfo.journalContact;
  }

  get journalDecision() {
    return this.args.journalDecisionInfo?.journalDecision;
  }

  get journalHasMultipleGoals() {
    return this.args.journalContactInfo.journalInfo.hasMultipleGoals;
  }

  @cached
  get availableJournalGoals() {
    let existingJournalGoal = this.args.journalDecisionInfo?.journalGoal;
    if (existingJournalGoal) {
      return [existingJournalGoal];
    }
    return this.args.journalContactInfo.journalInfo.goals;
  }

  get journalMainGoal() {
    return this.availableJournalGoals.length > 1
      ? this.availableJournalGoals.find((x) => !x.oneTimeGoalType)
      : this.availableJournalGoals[0];
  }

  get hasDecisionErrors() {
    return (
      this.alreadyAnotherNoDecisionForSameGoalError ||
      this.cantMakeYesDecisionWhenNoDecisionExistsError ||
      this.cantMakeNoDecisionWhenYesDecisionExistsError
    );
  }

  get alreadyAnotherNoDecisionForSameGoalError() {
    let selectedJournalGoalId = this.selectedJournalGoal?.id;
    return (
      this.noDecision &&
      this.journalDecision == null &&
      this.args.journalContactInfo.journalDecisionInfos.some(
        (x) =>
          x.journalGoal.id === selectedJournalGoalId &&
          !x.journalDecision.yesNo,
      )
    );
  }

  get cantMakeNoDecisionWhenYesDecisionExistsError() {
    let selectedJournalGoalId = this.selectedJournalGoal?.id;
    return (
      this.noDecision &&
      this.journalDecision == null &&
      this.args.journalContactInfo.journalDecisionInfos.some(
        (x) =>
          x.journalGoal.id === selectedJournalGoalId && x.journalDecision.yesNo,
      )
    );
  }

  get cantMakeYesDecisionWhenNoDecisionExistsError() {
    let selectedJournalGoalId = this.selectedJournalGoal?.id;
    return (
      this.yesDecision &&
      this.journalDecision == null &&
      this.args.journalContactInfo.journalDecisionInfos
        .filter((x) => x.journalGoal.id === selectedJournalGoalId)
        .some((x) => !x.journalDecision.yesNo)
    );
  }

  get alreadyAnotherNoDecisionForOtherGoalWarning() {
    let selectedJournalGoalId = this.selectedJournalGoal?.id;
    return (
      this.noDecision &&
      this.journalDecision == null &&
      this.args.journalContactInfo.journalDecisionInfos.some(
        (x) =>
          x.journalGoal.id !== selectedJournalGoalId &&
          !x.journalDecision.yesNo,
      )
    );
  }

  get selectedJournalGoal() {
    return (
      this.args.journalDecisionInfo?.journalGoal ||
      this.args.journalContactInfo.journalInfo.goalsById[
        this.changeset.get('journalDecisionJournalGoalId')
      ]
    );
  }

  get pledge() {
    return this.args.journalDecisionInfo?.pledge;
  }

  get existingPledge() {
    return this.args.journalContactInfo.existingPledge;
  }

  get hasExistingPledge() {
    return this.existingPledge != null;
  }

  get hasMultipleExistingPledges() {
    return this.args.journalContactInfo.pledges.length > 1;
  }

  get pledgeIncreased() {
    return this.changeset.get('journalDecisionPledgeIncreased');
  }

  get changeset(): any {
    if (this._changeset) {
      return this._changeset;
    }
    let { journalContact, journalDecision, pledge } = this;
    let journalGoalId: string | undefined =
      this.args.journalDecisionInfo?.journalGoal?.id;
    if (journalGoalId == null) {
      journalGoalId = this.journalMainGoal?.id;
      if (this.journalHasMultipleGoals) {
        let mainGoalAlreadyHasADecision =
          this.args.journalContactInfo.journalDecisionInfos.some(
            (x) => x.journalGoal.id === journalGoalId,
          );
        if (mainGoalAlreadyHasADecision) {
          journalGoalId = this.availableJournalGoals.find(
            (x) => x.id !== journalGoalId,
          )?.id;
        }
      }
    }
    let decisionChange = this.store.createRecord('journal-decision-change', {
      journalDecisionJournalGoalId: journalGoalId,
    });
    decisionChange.populateFromModels({
      journalContact,
      journalDecision,
      pledge,
    });
    let validation = journalDecisionChangeValidation(this.hasYtdGoalType);
    return (this._changeset = new Changeset(
      decisionChange,
      lookupValidator(validation),
      validation,
    ));
  }

  get decisionMade() {
    return this.changeset.get('decisionMade');
  }

  get showDecisionDate() {
    return this.decisionMade && !this.hasDecisionErrors;
  }

  get yesDecision() {
    return this.decisionMade && this.changeset.get('journalDecisionYesNo');
  }

  get noDecision() {
    return this.decisionMade && !this.changeset.get('journalDecisionYesNo');
  }

  get askWhichGoal() {
    return (
      this.journalHasMultipleGoals &&
      this.decisionMade &&
      this.journalDecision == null
    );
  }

  // when decision was yes with a pledge but now decision is no or none
  get askToKeepPledge() {
    return (
      this.pledge &&
      !this.yesDecision &&
      this.pledgeIsNotPartOfAnotherDecision &&
      this.canDeletePledge
    );
  }

  get canDeletePledge() {
    return (
      this.abilities.hasAbility('edit_pledges') ||
      (this.pledge?.isManual &&
        this.abilities.hasAbility('edit_manual_pledges'))
    );
  }

  get canEditPledgeNotEndDateFields() {
    return (
      this.abilities.hasAbility('edit_pledges') ||
      ((this.pledge == null || this.pledge.isManual) &&
        this.abilities.hasAbility('edit_manual_pledges'))
    );
  }

  get canEditPledgeEndDate() {
    return (
      this.abilities.hasAbility('edit_pledges') ||
      (this.abilities.hasAbility('edit_manual_pledges') &&
        (this.pledge == null || this.pledge?.isManual)) ||
      (this.abilities.hasAbility('edit_system_pledge_end_date') &&
        this.pledge != null &&
        !this.pledge.isManual)
    );
  }

  get showCantEditSystemPledge() {
    return (
      (this.abilities.hasAbility('edit_manual_pledges') ||
        this.abilities.hasAbility('edit_system_pledge_end_date')) &&
      this.pledge != null &&
      !this.pledge.isManual
    );
  }

  async findIfPledgeIsNotPartOfAnotherDecision() {
    if (this.pledgeIsNotPartOfAnotherDecision !== undefined) {
      return;
    } else if (this.pledge == null) {
      this.pledgeIsNotPartOfAnotherDecision = true;
    } else {
      this.disableSave = true;
      let results = (
        await this.store.query('journal-decision', {
          filter: { pledge_id: this.pledge.id },
        })
      ).slice();
      this.pledgeIsNotPartOfAnotherDecision = results.length <= 1;
      this.disableSave = false;
    }
  }

  get hasYtdGoalType() {
    return this.fundGoalService.hasYtdGoalType;
  }

  get showNumberMonths() {
    return (
      !this.hasYtdGoalType &&
      this.changeset.get('pledgeFrequency') === pledgeOneTimeFrequency
    );
  }

  get showEndDate() {
    return this.changeset.get('pledgeFrequency') !== pledgeOneTimeFrequency;
  }

  get frequencyOptions(): SelectOption[] {
    let options: SelectOption[] = [];
    Object.keys(pledgeFrequencyLabels).forEach((key) => {
      if (pledgeFrequencyLabels[key] !== 'Weekly') {
        options.push({
          val: Number(key),
          label: pledgeFrequencyLabels[key],
        });
      }
    });
    return options;
  }

  @action
  setSelectedJournalGoal(goal: JournalGoalModel) {
    this.changeset.set('journalDecisionJournalGoalId', goal.id);
    this.afterDecisionAndGoalSelection();
    if (this.hasDecisionErrors) {
      this.showValidationErrors = true;
    } else {
      this.showValidationErrors = false;
    }
  }

  @action
  setNoDecisionMade() {
    set(this.changeset, 'decisionMade', false);
    if (this.showValidationErrors) {
      this.changeset.validate();
    }
    this.askWhichPledgeToIncreaseFrom = false;
    this.askWhichPledgeToIncreaseTo = false;
    this.increaseFromPledge = undefined;
    this.increaseToPledge = undefined;
    this.findIfPledgeIsNotPartOfAnotherDecision();
    this.handleElementHeightChanged();
  }

  @action
  setDecisionYesNo(val: boolean) {
    setProperties(this.changeset, {
      decisionMade: true,
      journalDecisionYesNo: val,
    });
    if (this.changeset.get('journalDecisionDecisionDate') == null) {
      this.changeset.set('journalDecisionDecisionDate', new Date());
    }
    this.afterDecisionAndGoalSelection();
    if (this.hasDecisionErrors) {
      this.showValidationErrors = true;
    } else {
      this.showValidationErrors = false;
    }
    if (!val) {
      this.findIfPledgeIsNotPartOfAnotherDecision();
    }
    this.handleElementHeightChanged();
  }

  afterDecisionAndGoalSelection() {
    if (isEmpty(this.changeset.get('journalDecisionJournalGoalId'))) {
      this.changeset.set(
        'journalDecisionJournalGoalId',
        this.journalMainGoal?.id,
      );
    }
    if (this.yesDecision) {
      if (isEmpty(this.changeset.get('pledgeFrequency'))) {
        setProperties(this.changeset, {
          pledgeFrequency: this.selectedJournalGoal.oneTimeGoalType
            ? pledgeOneTimeFrequency
            : pledgeMonthlyFrequency,
        });
        this.handlePledgeFrequencyChanged();
      }
      if (
        this.hasExistingPledge &&
        (!this.journalHasMultipleGoals ||
          !this.selectedJournalGoal.oneTimeGoalType)
      ) {
        this.askAboutExistingPledge = true;
        this.existingPledgeDecision = undefined;
      }
    } else if (this.noDecision) {
      this.askWhichPledgeToIncreaseFrom = false;
      this.askWhichPledgeToIncreaseTo = false;
      this.increaseFromPledge = undefined;
      this.increaseToPledge = undefined;
    }
  }

  handleElementHeightChanged() {
    setTimeout(() => {
      this.args.onElementHeightChanged();
    });
  }

  @action
  setDeletePledge(val: boolean) {
    this.changeset.set('deletePledge', val);
  }

  get showPledge() {
    return (
      this.yesDecision &&
      isPresent(this.selectedJournalGoal) &&
      this.askWhichPledgeToIncreaseFrom === false &&
      this.askWhichPledgeToIncreaseTo === false &&
      this.increaseToPledge == null &&
      (this.askAboutExistingPledge == false ||
        isPresent(this.existingPledgeDecision))
    );
  }

  @action
  setExistingPledgeDecision(val: 'existing' | 'increase' | 'new') {
    this.existingPledgeDecision = val;
    this.askAboutExistingPledge = false;
    this.askWhichPledgeToIncreaseFrom = false;
    let existingPledge = this.existingPledge!;
    if (val === 'existing') {
      setProperties(this.changeset, {
        pledgeId: existingPledge.id,
        pledgeFrequency: existingPledge.frequency,
        pledgeStartDate: existingPledge.startDate,
        pledgeEndDate: existingPledge.endDate,
        pledgeAmount: existingPledge.amount,
        journalDecisionPledgeIncreased: false,
      });
    } else if (val === 'increase') {
      if (this.hasMultipleExistingPledges) {
        this.askWhichPledgeToIncreaseFrom = true;
      } else {
        setProperties(this.changeset, {
          pledgeId: null,
          pledgeFrequency: existingPledge.frequency,
          pledgeAmount: existingPledge.amount,
          pledgeStartDate: null,
          isIncrease: true,
          journalDecisionPledgeIncreased: true,
          journalDecisionPrevPledgeId: existingPledge.id,
        });
      }
    } else if (val === 'new') {
      setProperties(this.changeset, {
        pledgeAmount: undefined,
        journalDecisionPledgeIncreased: false,
      });
    }
    this.handlePledgeFrequencyChanged();
    this.handleElementHeightChanged();
  }

  @action
  setIncreaseFromPledge(pledge: PledgeModel) {
    this.increaseFromPledge = pledge;
    this.askWhichPledgeToIncreaseFrom = false;
    this.askWhichPledgeToIncreaseTo = true;
  }

  @action
  setIncreaseToNewPledge() {
    this.args.journalContactInfo.existingPledge = this.increaseFromPledge;
    setProperties(this.changeset, {
      pledgeId: null,
      pledgeFrequency: this.increaseFromPledge!.frequency,
      pledgeAmount: this.increaseFromPledge!.amount,
      pledgeStartDate: null,
      isIncrease: true,
      journalDecisionPledgeIncreased: true,
      journalDecisionPrevPledgeId: this.increaseFromPledge!.id,
    });
    this.askWhichPledgeToIncreaseFrom = false;
    this.askWhichPledgeToIncreaseTo = false;
  }

  @action
  setIncreaseToPledge(pledge: PledgeModel) {
    this.increaseToPledge = pledge;
    setProperties(this.changeset, {
      pledgeId: pledge.id,
      pledgeFrequency: pledge.frequency,
      pledgeAmount: pledge.amount,
      pledgeStartDate: pledge.startDate,
      isIncrease: true,
      journalDecisionPledgeIncreased: true,
      journalDecisionPrevPledgeId: this.increaseFromPledge!.id,
    });
    this.askWhichPledgeToIncreaseFrom = false;
    this.askWhichPledgeToIncreaseTo = false;
  }

  get showIncreaseAmount() {
    return this.increaseToPledge != null;
  }

  get increaseAmount() {
    return (
      (this.increaseToPledge?.amount || 0) -
      (this.increaseFromPledge?.amount || 0)
    );
  }

  get allPledges() {
    return sortByProperties(this.args.journalContactInfo.pledges, [
      'startDate:desc',
    ]);
  }

  get allPledgesExceptIncreaseFromPledge() {
    return sortByProperties(
      this.args.journalContactInfo.pledges.filter(
        (x) =>
          x.id !== this.increaseFromPledge?.id &&
          x.amount! > this.increaseFromPledge?.amount!,
      ),
      ['startDate:desc'],
    );
  }

  @cached
  get previousPledge() {
    if (isPresent(this.journalDecision?.prevPledgeInfo)) {
      return this.journalDecision.getPrevPledge(this.store);
    } else {
      return this.existingPledge;
    }
  }

  get changesetPledgeFrequencyLabel() {
    return pledgeFrequencyLabels[this.changeset.get('pledgeFrequency')];
  }

  @action
  handlePledgeFrequencyChanged() {
    if (this.changeset.get('pledgeFrequency') === pledgeOneTimeFrequency) {
      if (isEmpty(this.changeset.get('pledgeEndDateMonths'))) {
        this.changeset.set('pledgeEndDateMonths', '12');
      }
    }
  }

  get existingPledgeDecisionIsValid() {
    return (
      this.askAboutExistingPledge === false ||
      this.existingPledgeDecision != null ||
      !this.decisionMade
    );
  }

  get errorMessages() {
    let messages = [];
    if (this.alreadyAnotherNoDecisionForSameGoalError) {
      messages.push(
        "This person already made a No decision, so you don't need to create another No decision",
      );
    } else if (this.cantMakeNoDecisionWhenYesDecisionExistsError) {
      if (this.journalHasMultipleGoals) {
        messages.push(
          "There is already a Yes decision made for the same Journal Goal, so you can't make a No decision",
        );
      } else {
        messages.push(
          "There is already a Yes decision made, so you can't make a No decision",
        );
      }
    } else if (this.cantMakeYesDecisionWhenNoDecisionExistsError) {
      messages.push(
        'Delete the existing No decision before making a Yes decision',
      );
    } else if (!this.existingPledgeDecisionIsValid) {
      messages.push('Make a decision about the existing pledge');
    } else {
      for (let error of this.changeset.errors) {
        messages.push(error.validation);
      }
    }
    return messages;
  }

  get warningMessages() {
    let messages = [];
    if (this.alreadyAnotherNoDecisionForOtherGoalWarning) {
      messages.push(
        "A no decision was already made for the other goal. You can make a no decision for this goal but it's recommened you don't",
      );
    }
    return messages;
  }

  @action
  async save() {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.showValidationErrors = false;
    if (this.changeset.get('pledgeFrequency') === pledgeOneTimeFrequency) {
      this.changeset.set(
        'pledgeEndDate',
        calculateEndDateForOneTimePledge({
          startDate: this.changeset.get('pledgeStartDate'),
          endDateMonths: this.changeset.get('pledgeEndDateMonths'),
          hasYtdGoalType: this.hasYtdGoalType,
        }),
      );
    }
    await this.changeset.validate();
    if (this.changeset.get('isValid') && this.errorMessages.length === 0) {
      try {
        await this.changeset.save();

        // now re-load journalContactInfo
        let records = await this.store.query(
          'all-funds/journal-contact-detail',
          {
            filter: {
              journal_id: this.journalContact.journalId,
              journal_contact_id: this.journalContact.id,
            },
          },
        );
        let updatedJournalContactDetail = records.slice()[0];
        this.args.journalContactInfo.updateFromJournalContactDetailModel(
          updatedJournalContactDetail,
        );

        this.saving = false;
        this.args.onSaved();
      } catch (error) {
        this.saveError = error;
        this.saving = false;
      }
    } else {
      this.saving = false;
      this.showValidationErrors = true;
    }
  }
}
